import { useEffect, useRef, useState, useCallback } from "react";
import { IoMdDownload } from "react-icons/io";
import { FaArrowLeftLong } from "react-icons/fa6";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useSearchParams, useLocation ,useNavigate} from "react-router-dom";

import ReportFirstSection from "./ReportFirstSection";
import ReportSecondSection from "./ReportSecondSection";
import ReportThirdSection from "./ReportThirdSection";
import ReportFourthSection from "./ReportFourthSection";
import ReportFifthSection from "./ReportFifthSection";
import ReportSixthSection from "./ReportSixthSection";
import ReportRadarChart from "./ReportRadarChart";
import ReportAIInsights from "./ReportAIInsights";
import { apiFetch } from "../utils/apiFetch";

import "./Report.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Report = ({ hideUI = false ,setToggle,onClose}) => {
  const sectionRefs = useRef([]);
             

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate=useNavigate()

  // UI navigation
  const stateReport = location.state?.reportData || null;

  // PDF params
  const userId = searchParams.get("userId");
  const reportId = searchParams.get("reportId");

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  if (isMobile) {
    localStorage.setItem("fromMobileReports", "true");
  }

  return () => {
    localStorage.removeItem("fromMobileReports");
  };
}, []);


  /* ------------------------------------
     FETCH REPORT BY ID
  -------------------------------------*/
  const fetchReportById = useCallback(async (userId, reportId) => {
    try {
      const res = await apiFetch(
        `/idea-validation/report-list/${userId}`
      );

      if (!res.ok) throw new Error("Failed to fetch report list");

      const data = await res.json();
      if (!data.success || !data.reports?.length) return null;

      return data.reports.find(
        (r) => String(r.id) === String(reportId)
      );
    } catch (err) {
      console.error("❌ Error fetching report by ID:", err);
      return null;
    }
  }, []);

  /* ------------------------------------
     LOAD DATA
  -------------------------------------*/
  useEffect(() => {
    const load = async () => {
      try {
        // 1️⃣ UI flow
        if (stateReport) {
          setReportData(stateReport);
          return;
        }

        // 2️⃣ PDF flow
        if (userId && reportId) {
          const report = await fetchReportById(userId, reportId);
          setReportData(report);
          return;
        }

        throw new Error("No report source available");
      } catch (err) {
        console.error("❌ Report load failed:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [stateReport, userId, reportId, fetchReportById]);

  /* ------------------------------------
     DOWNLOAD PDF (UI ONLY)
  -------------------------------------*/
  const downloadReport = async () => {
    try {
      setToggle(false);
      onClose();
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 15;

      let yOffset = margin;

      for (let section of sectionRefs.current) {
        if (!section) continue;

        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#FFFFFF",
        });

        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (yOffset + imgHeight > pdfHeight - margin) {
          pdf.addPage();
          yOffset = margin;
        }

        pdf.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          0,
          yOffset,
          pdfWidth,
          imgHeight
        );

        yOffset += imgHeight + 5;
      }

      pdf.save("Idea Validation Report_A by Marsmate AI.pdf");
    } catch (error) {
      console.error("PDF download error:", error);
      alert("download feature is coming soon");
    }
  };

  /* ------------------------------------
     GUARDS
  -------------------------------------*/
  if (loading) return <div style={{ padding: 40 }}>Loading report…</div>;

  if (!reportData)
    return (
      <div style={{ padding: 40, color: "red" }}>
        Failed to load report data
      </div>
    );

  /* ------------------------------------
     RENDER
  -------------------------------------*/
  return (
    <div className="report-page-idea">
      {!hideUI && (
        <div className="download-container">
         <button
  className="back-btn-report"
  onClick={() => {
    const fromMobile = localStorage.getItem("fromMobileReports");
    localStorage.removeItem("fromMobileReports");
    onClose();
    setToggle(false);
    if (fromMobile === "true") {
      navigate(`/mobile/reports`);
    } else {
      navigate(-1);
    }
  }}
>
  <FaArrowLeftLong size={26} />
</button>


          <button className="download-btn" onClick={downloadReport}>
            Download <IoMdDownload size={26} />
          </button>
        </div>
      )}

      {/* 👀 Visible UI */}
      <div className="report-section">
        {[
          ReportFirstSection,
          ReportSecondSection,
          ReportThirdSection,
          ReportRadarChart,
          ReportAIInsights,
          ReportFourthSection,
          ReportFifthSection,
          ReportSixthSection,
        ].map((Component, index) => (
          <div key={index} className="pdf-section">
            <Component res={reportData} />
            {index === 0 && <hr className="hr" />}
          </div>
        ))}
      </div>

      {/* 🔒 Hidden PDF Layout */}
      <div className="pdf-only">
        {[
          ReportFirstSection,
          ReportSecondSection,
          ReportThirdSection,
          ReportRadarChart,
          ReportAIInsights,
          ReportFourthSection,
          ReportFifthSection,
          ReportSixthSection,
        ].map((Component, index) => (
          <div
            key={index}
            ref={(el) => (sectionRefs.current[index] = el)}
            className="pdf-section"
          >
            <Component res={reportData} />
            {index === 0 && <hr className="hr" />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Report;
